import { useMemo, useState } from 'react'
import {
  Button,
  Stack,
  Typography,
  Divider
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { FormProvider, useForm } from 'react-hook-form'

import ActionButtons from './components/ActionButtons'
import { useBatchOperations } from '../../use-batch-operations'

import { CATEGORIES_ROLES } from '../../roles-mapping'
import { useRolesModifications } from '../../store'
import { useUser } from 'store'
import CategorySection from './components/CategorySection/CategorySection'

type RoleState = 'add' | 'remove' | 'unchanged'

interface RolesManagementProps {
  onRefetch?: () => void
}

const RolesManagement = ({ onRefetch }: RolesManagementProps) => {
  const [rolesModifications, setRolesModifications] = useRolesModifications()
  const [error, setError] = useState<string>()
  const { t } = useTranslation()
  const { updateRoles, isLoading } = useBatchOperations({ onRefetch })
  const [user] = useUser()

  const methods = useForm({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  })

  const errorMessage = useMemo(() => {
    if (!error) {
      return null
    }
    const [, code] = error?.split('::') || []
    if (code) {
      return t(`errors.contact.${code}`)
    } else {
      return t('errors.contact.unknown')
    }
  }, [error, t])

  const filteredCategories = useMemo(() => {
    if (!user) return []
    
    return CATEGORIES_ROLES
      .filter(category => category.name !== 'admin')
      .map(category => ({
        ...category,
        roles: Object.fromEntries(
          Object.entries(category.roles)
            .filter(([role]) => user.admin || (user.roles && user.roles[role]))
        )
      }))
      .filter(category => Object.keys(category.roles).length > 0)
  }, [user])

  const handleRoleChange = (role: string, state: RoleState) => {
    setRolesModifications(prev => ({
      ...prev,
      [role]: state
    }))
  }

  const handleSaveRoles = async () => {
    try {
      await updateRoles()
      setRolesModifications({})
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error')
    }
  }

  return (
    <FormProvider {...methods}>
      <Stack justifyContent="space-between" sx={{ height: '100%', minHeight: 0 }}>
        {Boolean(errorMessage) && (
          <Typography variant="caption" color="error">{errorMessage}</Typography>
        )}

        <Stack
          gap={2}
          sx={{
            overflowY: 'auto',
            flex: 1,
            minHeight: 0,
            pr: 2,
            mr: -1,
            scrollbarWidth: 'none',
            px: 1,
            py: 1,
            '&::-webkit-scrollbar': {
              display: 'none'
            }
          }}
        >
          {filteredCategories.map(category => (
            <CategorySection
              key={category.name}
              mode="management"
              category={category}
              rolesState={rolesModifications}
              onRoleChange={handleRoleChange}
              compact={true}
              colorScheme="green"
            />
          ))}
        </Stack>

        <Stack spacing={2} flexShrink={0}>
          <Divider sx={{ borderColor: '#F7F7F7' }} />
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <ActionButtons onRefetch={onRefetch} />
            <Button
              disabled={isLoading}
              variant="contained"
              size="small"
              onClick={handleSaveRoles}
              sx={{
                backgroundColor: 'primary.main',
                height: '32px'
              }}
            >
              {isLoading ? (
                <>
                  <i className="fa-regular fa-spinner fa-spin" style={{ fontSize: '12px', marginRight: '4px' }} />
                  {t('auth.card_in_mass.loading')}
                </>
              ) : (
                t('auth.card_in_mass.form_save')
              )}
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </FormProvider>
  )
}

export default RolesManagement